import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useContainerHeight } from '@/common/hooks/useContainerHeight';
import { useContainerWidth } from '@/common/hooks/useContainerWidth';

interface CircularTimerProps {
  timeLeft: number;
  totalTime: number;
  phase: 'focus' | 'break';
  formattedTime: string;
}

export default function CircularTimer({ timeLeft, totalTime, phase, formattedTime }: CircularTimerProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(containerRef);
  const containerHeight = useContainerHeight(containerRef);

  const progress = ((totalTime - timeLeft) / totalTime) * 100;
  // Use the smaller dimension to maintain aspect ratio
  const size = Math.min(containerWidth, containerHeight);

  return (
    <div ref={containerRef} className="relative w-full h-full p-8">
      <div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        style={{ width: size, height: size }}
      >
        {/* Background circle */}
        <div className="w-full h-full rounded-full bg-gray-100"></div>

        {/* Progress circle */}
        <svg
          className="absolute top-0 left-0 transform -rotate-90"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          viewBox="0 0 100 100"
          width="100%"
        >
          <circle
            className="transition-all duration-1000 ease-linear"
            cx="50"
            cy="50"
            fill="none"
            r="45"
            stroke={phase === 'focus' ? '#FFC107' : '#4CAF50'}
            strokeDasharray={`${2 * Math.PI * 45}`}
            strokeDashoffset={`${(2 * Math.PI * 45 * (100 - progress)) / 100}`}
            strokeLinecap="round"
            strokeWidth="8"
          />
        </svg>

        {/* Timer Display */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
          <div
            className="font-bold text-gray-800"
            style={{ fontSize: `${size * 0.17}px` }}
          >
            {formattedTime}
          </div>
          <div
            className={`font-medium mt-2 ${
              phase === 'focus' ? 'text-yellow-600' : 'text-green-600'
            }`}
            style={{ fontSize: `${size * 0.06}px` }}
          >
            {phase === 'focus' ? t('pomodoro.focusTime') : t('pomodoro.breakTime')}
          </div>
        </div>
      </div>
    </div>
  );
}
